import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate } from "react-router-dom";
import {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import useThemeColor from "../../hooks/useThemeColor";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import "./../order/ManageOrderFilters.css";
import ProductSelectionModal from "../../components/CustomComponents/modal/ProductSelectionModal";

const ManageRecipes = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const dispatch = useDispatch();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [originalItemsCount, setOriginalItemsCount] = useState(0);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [compCurrency, setCompCurrency] = useState("GBP");
  const lastSelectedIndex = useRef(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const pageKey = "recipes";
  const defaultVisibleColumns = {
    title: true,
    sku: true,
    Price: true,
    deliveryPrice: false,
    takeAwayPrice: false,
    Reporting: false,
    sortOrder: true,
    status: true,
    rating: false
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "MenuItem_Orderby", label: "Sort Order" };

  // Filter options
  const defaultStatus = { value: "", label: "All Status" };

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  // Filter states
  const status = preferences.status || defaultStatus;

  const sortByOptions = [
    { value: "MenuItem_Orderby", label: "Sort Order" },
    { value: "MenuItem_ID", label: "ID" },
    { value: "MenuItem_Name", label: "Name" },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "InActive" },
  ];

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const getItemDisplayName = (item) => {
    if (!item) return "";
    const v = (p) => (p != null && String(p).trim() !== "" ? String(p).trim() : "");
    return (
      v(item.menuItem_DefaultVariantTitle) ||
      v(item.MenuItem_DefaultVariantTitle) ||
      v(item.menuItem_Name) ||
      v(item.MenuItem_Name) ||
      v(item.menuitem_name) ||
      ""
    );
  };

  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Fetch CompCurrency
  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data.data;
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }

    // Initialize filter states
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }

    // Fetch CompCurrency on component mount
    fetchCompCurrency();
  }, [dispatch, pageKey, fetchCompCurrency]);

  const fetchItemsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_RECIPES, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
          PageNumber: pageNumber,
          PageSize: pageSize.value,
          Status: status?.value !== "" ? String(status?.value) : ""
        },
      });

      const itemsData = response.data.data || response.data;

      setTotalPages(response.data.totalPages || 0);
      setTotalRecords(response.data.totalRecords || 0);
      if (Array.isArray(itemsData)) {
        setItems(itemsData);

        if (!search.trim()) {
          setOriginalItemsCount(itemsData.length);
        }
      } else {
        setItems([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setItems([]);
    }
  }, [search, sortBy, sortOrder, CompID, pageNumber, pageSize, status]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchItemsData();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchItemsData]);

  const handleDeleteItem = async (item) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${getItemDisplayName(item)}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await axiosInstance
        .delete(APIs.DELETE_ITEM, {
          params: {
            CompID,
            MenuItem_ID: item.menuItem_ID,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setItems((prevItems) =>
              prevItems.filter((i) => i.menuItem_ID !== item.menuItem_ID)
            );
            showSuccessToast("Item is Deleted.");
          } else {
            showErrorToast(response.data.message || "Deletion failed");
          }
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something Went Wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (itemId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);

        for (let i = start; i <= end; i++) {
          if (items[i]?.menuItem_ID) {
            newSelected.add(items[i].menuItem_ID);
          }
        }
      } else {
        if (newSelected.has(itemId)) {
          newSelected.delete(itemId);
        } else {
          newSelected.add(itemId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(items.map((item) => item.menuItem_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected items?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.delete(APIs.DELETE_ITEM, {
            params: {
              CompID,
              MenuItem_ID: itemId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchItemsData();
      showSuccessToast(`${selectedItems.size} items deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected items.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for editing.");
      return;
    }

    const selectedItemIds = Array.from(selectedItems);
    navigate(`/items/bulkEditItems`, { state: { selectedItemIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${publishStatus ? "publish" : "unpublish"} ${selectedItems.size} selected items?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedItems = items.map((item) =>
        selectedItems.has(item.menuItem_ID)
          ? { ...item, menuItem_Publish: publishStatus }
          : item
      );
      setItems(updatedItems);

      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
            CompID,
            MenuItem_ID: itemId,
            MenuItem_Publish: publishStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} items status updated successfully.`
      );
      setLoading(false);
    } catch (error) {
      showErrorToast("Failed to update selected items status.");
      fetchItemsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    setPageNumber(1);
  };

  const handleDragStart = (e, item) => {
    const index = items.findIndex((i) => i.menuItem_ID === item.menuItem_ID);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(overIndex, 0, draggedItem);
    newItems = newItems.map((item, index) => ({
      ...item,
      menuItem_Orderby: index + 1 + (pageNumber - 1) * pageSize.value,
    }));

    setItems(newItems);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_ITEM_SORT_ORDER, {
        CompID: CompID,
        Updates: newItems.map((item) => ({
          MenuItem_ID: item.menuItem_ID,
          MenuItem_Orderby: item.menuItem_Orderby,
        })),
      });
      fetchItemsData();
    } catch (error) {
      showErrorToast("Failed to update item order.");
      fetchItemsData();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const toggleItemStatus = async (menuItem_ID, currentStatus) => {
    try {
      const updatedItems = items.map((item) =>
        item.menuItem_ID === menuItem_ID
          ? { ...item, menuItem_Publish: !currentStatus }
          : item
      );
      setItems(updatedItems);

      await axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
        CompID: CompID,
        MenuItem_ID: menuItem_ID,
        MenuItem_Publish: !currentStatus,
      });
    } catch (error) {
      const revertedItems = items.map((item) =>
        item.menuItem_ID === menuItem_ID
          ? { ...item, menuItem_Publish: currentStatus }
          : item
      );
      setItems(revertedItems);
      showErrorToast("Failed to update item status.");
    }
  };

  // Check if any filter is applied
  const hasFiltersApplied = () => {
    return (
      search.trim() !== "" ||
      (status && status.value !== "")
    );
  };

  const renderNoDataMessage = () => {
    if (items.length === 0 && hasFiltersApplied()) {
      return (
        <div className="alert alert-info text-center mt-3">
          <i className="fa fa-info-circle me-2"></i>
          No recipes found matching your filter criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Recipes</li>
          </ol>
          <h1 className="page-header mb-0">Manage Recipes</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
            {!isChildBranch && (
              <button
                className="btn px-4"
                onClick={() => setShowProductModal(true)}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }}
              >
                <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
                Add Recipe
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {/* Show centered Add Item button only when no items exist and no filters are applied */}
        {!isChildBranch && !loading && items.length === 0 && !hasFiltersApplied() && originalItemsCount === 0 && (
          <AddButton onClick={() => setShowProductModal(true)} title="Add Recipe" />
        )}

        {/* Show filter bar when: items exist, filters are applied, or we've loaded data before */}
        {(!loading && (items.length > 0 || hasFiltersApplied() || originalItemsCount > 0)) && (
          <>
            <div className="card border-0">
              <div className="tab-content p-3">
                <div className="tab-pane fade show active" id="allTab">
                  {/* Enhanced Filter Section */}
                  <div className="mb-4">
                    <div
                      className="card filter-card"
                      style={{
                        position: "relative",
                        zIndex: 1,
                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: `0 4px 20px ${themeColor}40`
                      }}
                    >
                      <div className="card-body p-3" >
                        {/* Filter Header */}

                        <div className="row g-3 align-items-center">
                          {/* Search Field */}
                          <div className="col-xl-3 col-lg-3 col-md-6">
                            <div className="search-input filter-control">
                              <input
                                type="text"
                                className="filter-input"
                                value={search}
                                onChange={(e) => {
                                  setSearch(e.target.value);
                                  setPageNumber(1);
                                }}
                                placeholder="Search by name, category..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                              />
                            </div>
                          </div>

                          {/* Status Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={status}
                                onChange={(val) => {
                                  dispatch(
                                    setFilterField({
                                      pageKey,
                                      field: "status",
                                      value: val,
                                    })
                                  );
                                  setPageNumber(1);
                                }}
                                options={statusOptions}
                                styles={dropdownStyles}
                                placeholder="All Status"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* Order By Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={sortBy}
                                onChange={(val) =>
                                  dispatch(setSortBy({ pageKey, sortBy: val }))
                                }
                                options={sortByOptions}
                                styles={dropdownStyles}
                                placeholder="Order By"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* Sort Order Dropdown */}
                          <div className="col-xl-1 col-lg-1 col-md-6">
                            <div className="filter-control">
                              <SortOrderDropDownSelection
                                defaultValue={sortOrder}
                                onChange={(val) =>
                                  dispatch(setSortOrder({ pageKey, sortOrder: val }))
                                }
                              />
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="col-xl-2 col-lg-2 col-md-12">
                            <div className="d-flex gap-2">
                              <button
                                className="clear-btn"
                                onClick={clearFilters}
                                title="Clear all filters"
                              >
                                <i className="fa fa-times me-1"></i>
                                Filter
                              </button>

                              <ColumnSelection
                                visibleColumns={visibleColumns}
                                setVisibleColumns={(cols) =>
                                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                                }
                              />
                            </div>
                          </div>

                        </div>

                      </div>
                    </div>
                  </div>

                  {/* Selection Info and Actions (Shown only when items are selected) */}
                  {selectedItems.size > 0 && (
                    <div className="mb-3">
                      <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                        <div className="d-flex align-items-center gap-3">
                          <span className="fw-bold text-primary">
                            {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
                          </span>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleDeselectAll}
                          >
                            Clear Selection
                          </button>
                        </div>

                        <div className="d-flex gap-2">
                          {!isChildBranch && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={handleBulkEdit}
                              title="Edit Selected Items"
                            >
                              <i className="fa fa-edit me-1"></i>
                              Edit
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleBulkStatusChange(true)}
                            title="Publish Selected Items"
                          >
                            <i className="fa fa-check me-1"></i>
                            Publish
                          </button>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleBulkStatusChange(false)}
                            title="Unpublish Selected Items"
                          >
                            <i className="fa fa-times me-1"></i>
                            Unpublish
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={handleBulkDelete}
                            title="Delete Selected Items"
                          >
                            <i className="fa fa-trash me-1"></i>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {renderNoDataMessage()}

                  {Array.isArray(items) && items.length > 0 && (
                    <>
                      <div
                        className="table-responsive table-container"
                        style={{
                          minHeight: "200px",
                          maxHeight: "450px",
                          overflowY: "auto",
                          overflowX: "auto",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <table className="table table-hover" style={{ position: "relative" }}>
                          <thead
                            className="bg-light sticky-header"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10000,
                              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          >
                            <tr>
                              <th className="pt-2 pb-2">
                                <input
                                  type="checkbox"
                                  onChange={handleSelectAll}
                                  checked={
                                    items.length > 0 &&
                                    selectedItems.size === items.length
                                  }
                                />
                              </th>
                              {visibleColumns.title && (
                                <th className="pt-2 pb-2">Title</th>
                              )}
                              {visibleColumns.sku && (
                                <th className="pt-2 pb-2">SKU</th>
                              )}

                              {visibleColumns.Price && (
                                <th className="pt-2 pb-2 text-center">
                                  Price
                                </th>
                              )}

                              {visibleColumns.deliveryPrice && (
                                <th className="pt-2 pb-2 text-center">
                                  Delivery Price
                                </th>
                              )}

                              {visibleColumns.takeAwayPrice && (
                                <th className="pt-2 pb-2 text-center">
                                  TakeAway Price
                                </th>
                              )}

                              {visibleColumns.Reporting && (
                                <th className="pt-2 pb-2">
                                  Reporting
                                </th>
                              )}

                              {visibleColumns.sortOrder && (
                                <th className="pt-2 pb-2 text-center">
                                  Sort Order
                                </th>
                              )}

                              {visibleColumns.rating && (
                                <th className="pt-2 pb-2 text-center">
                                  Rating
                                </th>
                              )}

                              {visibleColumns.status && (
                                <th className="pt-2 pb-2 text-center">
                                  Status
                                </th>
                              )}

                              <th className="pt-2 pb-2 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => (
                              <tr
                                key={item.menuItem_ID}
                                draggable={false}
                                onDragStart={(e) => {
                                  // Only allow dragging from the grab icon
                                  if (!e.target.classList.contains('fa-bars')) {
                                    e.preventDefault();
                                  }
                                }}
                                onDragEnter={(e) => handleDragEnter(e, index)}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                              >
                                <td 
                                  className="align-middle"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.has(
                                      item.menuItem_ID
                                    )}
                                    onChange={(e) =>
                                      handleSelectItem(
                                        item.menuItem_ID,
                                        index,
                                        e
                                      )
                                    }
                                    data-chkbox-shiftsel="type1"
                                  />
                                </td>

                                {visibleColumns.title && (
                                  <td
                                    className="align-middle"
                                    onClick={() => {
                                      if (isChildBranch) return;
                                      navigate(`/recipes/createRecipe/${item.menuItem_ID}`);
                                    }}
                                    style={{ cursor: isChildBranch ? "default" : "pointer" }}
                                  >
                                    {getItemDisplayName(item)}
                                  </td>
                                )}

                                {visibleColumns.sku && (
                                  <td
                                    className="align-middle"
                                    onClick={() => {
                                      if (isChildBranch) return;
                                      navigate(`/recipes/createRecipe/${item.menuItem_ID}`);
                                    }}
                                    style={{ cursor: isChildBranch ? "default" : "pointer" }}
                                  >
                                    {item.menuItem_SKU}
                                  </td>
                                )}

                                {visibleColumns.Price && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() => {
                                      if (isChildBranch) return;
                                      navigate(`/recipes/createRecipe/${item.menuItem_ID}`);
                                    }}
                                    style={{ cursor: isChildBranch ? "default" : "pointer" }}
                                  >
                                    {getCurrencySymbol(compCurrency)} {" "}
                                    {item.menuItem_SalePrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}

                                {visibleColumns.deliveryPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() => {
                                      if (isChildBranch) return;
                                      navigate(`/recipes/createRecipe/${item.menuItem_ID}`);
                                    }}
                                    style={{ cursor: isChildBranch ? "default" : "pointer" }}
                                  >
                                    {getCurrencySymbol(compCurrency)} {" "}
                                    {item.menuItem_DeliveryPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}

                                {visibleColumns.takeAwayPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() => {
                                      if (isChildBranch) return;
                                      navigate(`/recipes/createRecipe/${item.menuItem_ID}`);
                                    }}
                                    style={{ cursor: isChildBranch ? "default" : "pointer" }}
                                  >
                                    {getCurrencySymbol(compCurrency)} {" "}
                                    {item.menuItem_TakeAwayPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}

                                {visibleColumns.Reporting && (
                                  <td
                                    className="align-middle"
                                    onClick={() => {
                                      if (isChildBranch) return;
                                      navigate(`/recipes/createRecipe/${item.menuItem_ID}`);
                                    }}
                                    style={{ cursor: isChildBranch ? "default" : "pointer" }}
                                  >
                                    {item.rootCat_Name}
                                  </td>
                                )}

                                {visibleColumns.sortOrder && (
                                  <td 
                                    className="align-middle text-center"
                                  >
                                    {item.menuItem_Orderby}
                                  </td>
                                )}

                                {visibleColumns.rating && (
                                  <td 
                                    className="align-middle text-center"
                                  >
                                    {item.menuItem_Rating}
                                  </td>
                                )}

                                {visibleColumns.status && (
                                  <td 
                                    className="align-middle text-center"
                                  >
                                    <div
                                      className={`toggle-switch ${item.menuItem_Publish ? "active" : ""}`}
                                    >
                                      <label className="switch">
                                        <input
                                          type="checkbox"
                                          checked={item.menuItem_Publish}
                                          onChange={() =>
                                            toggleItemStatus(
                                              item.menuItem_ID,
                                              item.menuItem_Publish
                                            )
                                          }
                                        />
                                        <span
                                          className="slider round"
                                          style={{
                                            backgroundColor: item.menuItem_Publish ? themeColor : '#ccc'
                                          }}
                                        ></span>
                                      </label>
                                    </div>
                                  </td>
                                )}

                                <td className="align-middle text-center">
                                  <div className="d-flex justify-content-center gap-2">
                                    {/* Drag Handle */}
                                    <i
                                      className="fas fa-bars text-secondary"
                                      style={{ 
                                        cursor: "grab",
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        border: '1px solid #6c757d',
                                        backgroundColor: 'transparent',
                                        transition: 'all 0.2s ease'
                                      }}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, item)}
                                      onDragEnd={handleDragEnd}
                                      title="Drag to reorder"
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#6c757d';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#6c757d';
                                        e.currentTarget.style.transform = 'scale(1)';
                                      }}
                                    ></i>

                                    {/* Edit Icon */}
                                    {!isChildBranch && (
                                      <Link
                                        to={`/recipes/createRecipe/${item.menuItem_ID}`}
                                        className="btn btn-sm btn-outline-primary"
                                        title="Edit Recipe"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '6px',
                                          border: `1px solid ${themeColor}`,
                                          color: themeColor,
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = themeColor;
                                          e.currentTarget.style.color = 'white';
                                          e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.color = themeColor;
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                      >
                                        <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                      </Link>
                                    )}

                                    {/* Delete Icon */}
                                    <button
                                      onClick={() => handleDeleteItem(item)}
                                      className="btn btn-sm btn-outline-danger"
                                      title="Delete Item"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        border: '1px solid #dc3545',
                                        color: '#dc3545',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#dc3545';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#dc3545';
                                        e.currentTarget.style.transform = 'scale(1)';
                                      }}
                                    >
                                      <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <Pagination
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                  setPageSize={setPageSize}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSelectProduct={() => {}} // Not used anymore, but kept for compatibility
        compID={CompID}
      />

    </div>
  );
};

export default ManageRecipes;
